import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment'; // Already present
import { List, Button, Modal, Form, Input, message, Spin, Empty, Popconfirm, Space, Alert, Typography, Tooltip, Card, Tag } from 'antd'; 
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext'; // Already present
import StructuredFamilyHistoryForm from './StructuredFamilyHistoryForm';

const { TextArea } = Input;
const { Text } = Typography;

const FamilyHistorySection = ({ patientId }) => {
  const [freeTextHistory, setFreeTextHistory] = useState([]);
  const [structuredHistory, setStructuredHistory] = useState({ conditions: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFreeTextModalVisible, setIsFreeTextModalVisible] = useState(false);
  const [isChecklistModalVisible, setIsChecklistModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null); 
  const [form] = Form.useForm(); 
  const [submitLoading, setSubmitLoading] = useState(false);
  const [lastReviewed, setLastReviewed] = useState(null); // Added state
  const { user } = useAuth(); // Already present

  const fetchHistory = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      // Populate the new review field
      const response = await axios.get(`/patients/${patientId}?populate=familyHistoryLastReviewed.user`);
      const patientData = response?.data?.data?.patient;

      if (!patientData) {
        console.error("Error fetching family history: Invalid data structure received", response.data);
        throw new Error("Received invalid patient data structure from server.");
      }

      console.log("Fetched patient data for Family History:", patientData);
      setFreeTextHistory(patientData?.familyHistory || []);
      setStructuredHistory(patientData?.structuredFamilyHistory || { conditions: [] });
      setLastReviewed(patientData?.familyHistoryLastReviewed || null); // Set review status
    } catch (err) {
      console.error("Error fetching family history:", err);
       if (err.response) {
        console.error("Error Response Data:", err.response.data);
        console.error("Error Response Status:", err.response.status);
        setError(`Server Error ${err.response.status}: ${err.response.data?.message || 'Unknown server error'}`);
      } else if (err.request) {
        console.error("Error Request:", err.request);
        setError('No response received from server. Check network or backend status.');
      } else {
        console.error('Error Message:', err.message);
        setError(`Request setup error: ${err.message}`);
      }
      setFreeTextHistory([]);
      setStructuredHistory({ conditions: [] });
      setLastReviewed(null); // Clear on error
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const showFreeTextModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      const currentItem = freeTextHistory.find(h => h._id ? h._id === item._id : (h.relationship === item.relationship && h.condition === item.condition));
      form.setFieldsValue(currentItem);
    } else {
      form.resetFields();
    }
    setIsFreeTextModalVisible(true);
  };

  const handleFreeTextCancel = () => {
    setIsFreeTextModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleFreeTextFinish = async (values) => {
    setSubmitLoading(true);
    setError(null);
    let updatedHistory;
    let isEdit = !!editingItem;

    if (isEdit) {
      const index = freeTextHistory.findIndex(item => item._id ? item._id === editingItem._id : (item.relationship === editingItem.relationship && item.condition === editingItem.condition));
       if (index > -1) {
           updatedHistory = [...freeTextHistory];
           updatedHistory[index] = { ...(freeTextHistory[index]), ...values };
       } else {
           message.error("Could not find the original item to update. Please refresh.");
           setSubmitLoading(false);
           return;
       }
    } else {
      updatedHistory = [...freeTextHistory, values];
    }

    try {
      await axios.patch(`/patients/${patientId}`, { familyHistory: updatedHistory });
      message.success(`Free text family history ${isEdit ? 'updated' : 'added'} successfully!`);
      handleFreeTextCancel();
      setFreeTextHistory(updatedHistory);
    } catch (err) {
      console.error("Error saving free text family history:", err);
      message.error(err.response?.data?.message || 'Failed to save free text family history.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const showChecklistModal = () => {
      setIsChecklistModalVisible(true);
  };

  const handleChecklistCancel = () => {
      setIsChecklistModalVisible(false);
  };

  const handleChecklistSaveSuccess = () => {
      setIsChecklistModalVisible(false);
      fetchHistory();
  };

  const handleDelete = async (itemToDelete) => {
     const updatedHistory = freeTextHistory.filter(item =>
         item._id ? item._id !== itemToDelete._id : !(item.relationship === itemToDelete.relationship && item.condition === itemToDelete.condition)
     );
     try {
        setLoading(true);
        await axios.patch(`/patients/${patientId}`, { familyHistory: updatedHistory });
        message.success('Family history item deleted successfully!');
        setFreeTextHistory(updatedHistory);
     } catch (err) {
        console.error("Error deleting family history item:", err);
        message.error(err.response?.data?.message || 'Failed to delete item.');
     } finally {
        setLoading(false);
      }
  };

  // --- Mark as Reviewed Logic ---
  const handleMarkReviewed = async () => {
    if (!user || !user._id) {
      message.error("Cannot mark as reviewed: User not logged in.");
      return;
    }
    const reviewData = { user: user._id, date: new Date().toISOString() };
    setLoading(true);
    try {
      await axios.patch(`/patients/${patientId}`, { familyHistoryLastReviewed: reviewData });
      message.success('Family History marked as reviewed.');
      setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
    } catch (err) {
      console.error("Error marking family history as reviewed:", err);
      message.error(err.response?.data?.message || 'Failed to mark as reviewed.');
    } finally {
      setLoading(false);
    }
  };

  const canModify = user && ['Admin', 'MD', 'Nursing', 'Medical Assistant'].includes(user.role);

  const displayConditions = structuredHistory?.conditions || [];

  return (
    <Spin spinning={loading}>
        {/* Removed top Title level 4 */}
        {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

        {/* Standard Action Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
            <Space>
               {/* Standardized Buttons */}
               {canModify && (
                  <Button
                    type="primary"
                    icon={<HistoryOutlined />} // Standardized icon
                    onClick={showChecklistModal}
                  >
                    Update Family History Checklist
                  </Button>
               )}
               {canModify && (
                 <Button
                   icon={<PlusOutlined />}
                   onClick={() => showFreeTextModal(null)}
                 >
                   Add Specific Relative History
                 </Button>
               )}
               {/* Added Mark as Reviewed Button */}
               {canModify && (
                 <Tooltip title="Mark this section as reviewed by you right now">
                   <Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}>
                     Mark as Reviewed
                   </Button>
                 </Tooltip>
               )}
            </Space>
             {/* Added Review Status Display */}
             <div style={{ textAlign: 'right' }}>
               {lastReviewed?.date ? (
                 <Text type="secondary" style={{ fontSize: '0.85em' }}>
                   Last Reviewed by {lastReviewed.user?.name || 'Unknown User'} on {moment(lastReviewed.date).format('L LT')}
                 </Text>
               ) : (
                 <Text type="secondary" style={{ fontSize: '0.85em' }}>Not yet reviewed</Text>
               )}
             </div>
        </div>

        {/* Standardized Checklist Card */}
        <Card size="small" title="Family History Checklist Summary" style={{ marginBottom: 16, background: '#fafafa' }}>
            {loading && !error ? <Spin size="small" /> : (
                displayConditions && displayConditions.length > 0
                    ? <Space direction="vertical" align="start">
                        {displayConditions.map(condition => (
                          <Tag color="blue" key={condition}>{condition}</Tag>
                        ))}
                      </Space>
                    : <Text type="secondary">No checklist items selected.</Text>
            )}
        </Card>

        {/* Standardized Title Margins */}
        <Text strong style={{ fontSize: '1em', display: 'block', marginTop: '20px', marginBottom: '10px' }}>Specific Relative History</Text>
        <List
            itemLayout="horizontal"
            dataSource={freeTextHistory}
            locale={{ emptyText: <Empty description="No specific relative history recorded." /> }}
            renderItem={item => (
            <List.Item
                key={item._id || `${item.relationship}-${item.condition}-${Math.random()}`}
                actions={canModify ? [
                <Tooltip title="Edit Item" key={`edit-${item._id || item.condition}`}>
                    <Button icon={<EditOutlined />} onClick={() => showFreeTextModal(item)} size="small" />
                </Tooltip>,
                <Tooltip title="Delete Item" key={`delete-${item._id || item.condition}`}>
                    <Popconfirm title="Are you sure delete this item?" onConfirm={() => handleDelete(item)} okText="Yes" cancelText="No">
                        <Button icon={<DeleteOutlined />} danger size="small" />
                    </Popconfirm>
                </Tooltip>
                ] : []}
            >
                <List.Item.Meta
                title={`${item.relationship || 'N/A'}: ${item.condition || 'N/A'}`}
                description={item.notes || 'No additional notes.'}
                />
            </List.Item>
            )}
        />

        <Modal title={editingItem ? 'Edit Specific Relative History' : 'Add Specific Relative History'} open={isFreeTextModalVisible} onCancel={handleFreeTextCancel} footer={null} destroyOnClose width={600}>
            <Spin spinning={submitLoading}>
            <Form form={form} layout="vertical" onFinish={handleFreeTextFinish}>
                <Form.Item name="relationship" label="Relationship" rules={[{ required: true, message: 'Relationship is required.' }]}>
                  <Input placeholder="e.g., Mother, Father, Paternal Grandmother" />
                </Form.Item>
                <Form.Item name="condition" label="Condition / Diagnosis" rules={[{ required: true, message: 'Condition is required.' }]}>
                  <Input placeholder="e.g., Type 2 Diabetes, Hypertension, Breast Cancer" />
                </Form.Item>
                <Form.Item name="notes" label="Notes (Optional)">
                  <TextArea rows={3} placeholder="e.g., Age of onset, severity, etc." />
                </Form.Item>
                <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                    <Button onClick={handleFreeTextCancel} style={{ marginRight: 8 }}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={submitLoading}>{editingItem ? 'Save Changes' : 'Add Item'}</Button>
                </Form.Item>
            </Form>
            </Spin>
        </Modal>

        <Modal title="Update Family History Checklist" open={isChecklistModalVisible} onCancel={handleChecklistCancel} footer={null} destroyOnClose width={700}>
          {isChecklistModalVisible && (
              <StructuredFamilyHistoryForm patientId={patientId} onSaveSuccess={handleChecklistSaveSuccess} onCancel={handleChecklistCancel} />
          )}
        </Modal>
    </Spin>
  );
};

export default FamilyHistorySection;
